import { ChevronDown, History } from 'lucide-react';
import type { SimulationBatch } from '@/types/evaluator';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ALL_POPULATIONS } from '../constants';
import { getAgentVersionDisplay, getFriendlyRunName, getSimulationOutcome, getStatusLabel } from '../utils';

export function ResultsToolbar({
  runs,
  selectedRun,
  populations,
  selectedPopulation,
  onSelectRun,
  onPopulationChange,
  onOpenHistory,
}: {
  runs: SimulationBatch[];
  selectedRun: SimulationBatch | null;
  populations: string[];
  selectedPopulation: string;
  onSelectRun: (id: string) => void;
  onPopulationChange: (population: string) => void;
  onOpenHistory: () => void;
}) {
  const selectedPopulationLabel = selectedPopulation === ALL_POPULATIONS ? 'All populations' : selectedPopulation;

  return (
    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="max-w-[280px] justify-between">
              <span className="truncate">{selectedRun ? getFriendlyRunName(selectedRun) : 'Select run'}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[360px]">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Recent simulations</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {runs.slice(0, 12).map((run) => (
              <DropdownMenuItem
                key={run.id}
                className="flex cursor-pointer items-start gap-3 py-2"
                onClick={() => onSelectRun(run.id)}
              >
                <span className={cn(
                  'mt-1 h-2 w-2 shrink-0 rounded-full',
                  getSimulationOutcome(run.status) === 'failed'
                    ? 'bg-red-500'
                    : getSimulationOutcome(run.status) === 'cancelled'
                      ? 'bg-muted-foreground/50'
                      : 'bg-emerald-500',
                )} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{getFriendlyRunName(run)}</span>
                  <span className="block text-xs text-muted-foreground">
                    {(() => {
                      const agentVersion = getAgentVersionDisplay(run);
                      return [agentVersion.version, agentVersion.tag, getStatusLabel(run.status)].filter(Boolean).join(' · ');
                    })()}
                  </span>
                </span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" onClick={onOpenHistory}>
              <History className="mr-2 h-4 w-4" />
              View all history
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="max-w-[220px] justify-between">
              <span className="truncate">{selectedPopulationLabel}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Population</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" onClick={() => onPopulationChange(ALL_POPULATIONS)}>
              All populations
            </DropdownMenuItem>
            {populations.map((population) => (
              <DropdownMenuItem
                key={population}
                className="cursor-pointer"
                onClick={() => onPopulationChange(population)}
              >
                {population}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" size="sm" onClick={onOpenHistory}>
          <History className="h-4 w-4" />
          History
        </Button>
    </div>
  );
}
